import { buildApiUrl } from "../helpers/base_url";

export type SupportCasePriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";
export type SupportCaseStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "WAITING_ON_CLIENT"
  | "WAITING_ON_VENDOR"
  | "RESOLVED"
  | "CLOSED";

export interface CreateSupportCasePayload {
  subject: string;
  description: string;
  priority: SupportCasePriority;
  attachmentUrl?: string;
}

export const createSupportCase = async (payload: CreateSupportCasePayload) => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("You must be logged in to create a support case.");
  }

  const response = await fetch(buildApiUrl("/api/support/cases"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const errorMessage =
      data?.message ||
      (typeof data === "string" ? data : "Failed to create support case");
    throw new Error(errorMessage);
  }

  return data;
};

export interface SupportCaseSummary {
  id: number;
  caseNumber: string;
  subject: string;
  status: SupportCaseStatus;
  priority: SupportCasePriority;
  createdByType: string;
  lastResponderType: string;
  createdAt: string;
  latestActivityAt?: string;
}

export const getMySupportCases = async (
  status?: SupportCaseStatus | ""
): Promise<SupportCaseSummary[]> => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("You must be logged in to view support cases.");
  }

  const params = status ? `?status=${encodeURIComponent(status)}` : "?status=";
  const response = await fetch(
    buildApiUrl(`/api/support/cases/my/summary${params}`),
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const errorMessage =
      data?.message ||
      (typeof data === "string" ? data : "Failed to fetch support cases");
    throw new Error(errorMessage);
  }

  return data?.data || [];
};

